import type { SpendTransaction } from '@/services/vendorDetails'

export interface VendorSpendCacheEntry {
  startDate: string
  endDate: string
  transactions: SpendTransaction[]
  displayName: string
  fetchedAt: number
}

const MAX_ENTRIES = 10
const TTL_MS = 3 * 60 * 60 * 1000

const cache = new Map<string, VendorSpendCacheEntry>()

function isExpired(entry: VendorSpendCacheEntry): boolean {
  return Date.now() - entry.fetchedAt > TTL_MS
}

/** Read a vendor's cached transactions, refreshing LRU order. Returns null if missing or stale. */
export function getVendorSpendFromCache(vendorId: string): VendorSpendCacheEntry | null {
  const entry = cache.get(vendorId)
  if (!entry) return null
  if (isExpired(entry)) {
    cache.delete(vendorId)
    return null
  }
  cache.delete(vendorId)
  cache.set(vendorId, entry)
  return entry
}

/** Store or replace a vendor's transaction cache entry. Evicts oldest when over capacity. */
export function setVendorSpendCache(
  vendorId: string,
  data: Omit<VendorSpendCacheEntry, 'fetchedAt'>
): void {
  if (cache.has(vendorId)) {
    cache.delete(vendorId)
  }
  cache.set(vendorId, { ...data, fetchedAt: Date.now() })
  while (cache.size > MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value
    if (oldestKey === undefined) break
    cache.delete(oldestKey)
  }
}

/** @internal Test helper — clears module cache between tests. */
export function clearVendorSpendCacheForTests(): void {
  cache.clear()
}
